import { ApiService } from '../Api/ApiService';

export interface UploadResponse {
  id: string;
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  path: string;
  public_url: string;
  uploaded_at: string;
}

export interface UploadResponseMapped {
  url: string;
  publicUrl: string;
  filename: string;
  size: number;
  mimeType: string;
}

export class UploadService {
  private static instance: UploadService;
  private apiService: ApiService;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): UploadService {
    if (!UploadService.instance) {
      UploadService.instance = new UploadService();
    }
    return UploadService.instance;
  }

  async uploadImage(file: File): Promise<UploadResponseMapped> {
    try {
      console.log('📤 UploadService: Starting image upload', { 
        filename: file.name, 
        size: this.formatFileSize(file.size), 
        type: file.type 
      });
      
      const formData = new FormData();
      formData.append('image', file);

      // FormData voor Content-Type header verwijderen
      // Browser stelt automatisch multipart/form-data in
      const response = await this.apiService.post<{code: number, status: string, data: UploadResponse}>('/api/v2/upload/image', formData);
      
      console.log('📤 UploadService: Upload successful', response);
      
      // API response'unu map et - data objesi içindeki veriyi kullan
      const mappedResponse: UploadResponseMapped = {
        url: response.data.path, // path'i url olarak kullan (public_url yerine)
        publicUrl: response.data.public_url,
        filename: response.data.filename,
        size: response.data.size,
        mimeType: response.data.mime_type
      };
      
      console.log('📤 UploadService: Mapped response', mappedResponse);
      return mappedResponse;
    } catch (error) {
      console.error('❌ UploadService: uploadImage failed', error);
      throw new Error('Er is een fout opgetreden bij het uploaden van de afbeelding');
    }
  }

  async uploadFile(file: File, type: 'document' | 'image' | 'video' = 'document'): Promise<UploadResponseMapped> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await this.apiService.post<{code: number, status: string, data: UploadResponse}>('/api/v2/upload/file', formData);

      // API response'unu map et - data objesi içindeki veriyi kullan
      const mappedResponse: UploadResponseMapped = {
        url: response.data.path, // path'i url olarak kullan (public_url yerine)
        publicUrl: response.data.public_url,
        filename: response.data.filename,
        size: response.data.size,
        mimeType: response.data.mime_type
      };

      return mappedResponse;
    } catch (error) {
      console.error('❌ UploadService: uploadFile failed', error);
      throw new Error('Er is een fout opgetreden bij het uploaden van het bestand');
    }
  }

  // Dosya boyutu formatlaması
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Dosya tipi kontrolü
  isValidImageType(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    return validTypes.includes(file.type);
  }

  // Dosya boyutu kontrolü (MB cinsinden)
  isValidFileSize(file: File, maxSizeMB: number): boolean {
    return file.size <= maxSizeMB * 1024 * 1024;
  }

  // Dosya bilgilerini al
  getFileInfo(file: File) {
    return {
      name: file.name,
      size: this.formatFileSize(file.size),
      type: file.type,
      lastModified: new Date(file.lastModified).toLocaleDateString('nl-NL')
    };
  }

  // Dosya uzantısını al
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  // Dosya tipini kontrol et
  getFileType(file: File): 'image' | 'document' | 'video' | 'audio' | 'other' {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    if (file.type.includes('document') || file.type.includes('pdf') || file.type.includes('text')) return 'document';
    return 'other';
  }
} 